import React, { useEffect, useContext } from 'react';
import axios from '@js/modules/axios/index';
import { useForm, useFieldArray } from 'react-hook-form';

import { InvestorRegistrationContainer } from '@js/components/common/investorRegistrations/InvestorRegistrationContainer';
import { SingleCorporateOwner } from '@js/components/corporateRegistrations/SingleCorporateOwner';
import { SingleOwner, SingleOwnerFormData } from '@js/interfaces/index';
import { usePath } from '@js/hooks/usePath';
import { Context } from '@js/routes';
import { CONFIRM_MESSAGE } from '@js/components/corporateRegistrations/constants';
import Toast from '@js/modules/toast';

export const CorporateOwner = () => {
  const { setIsLoading, isEdit, setIsEdit, setShouldSkipCompatibility } = useContext(Context);

  const initializedSingleOwnerForm: SingleOwnerFormData = {
    is_representative: null,
    category: '',
    last_name_kanji: '',
    first_name_kanji: '',
    last_name_kana: '',
    first_name_kana: '',
    gender: '',
    birthday_year: '1990',
    birthday_month: '1',
    birthday_day: '1',
    name: '',
    name_kana: '',
    corporate_number: '',
    corporate_relationship: '',
    postal_code: '',
    prefecture_name: '選択して下さい',
    city: '',
    street: '',
    building: ''
  };
  const { register, handleSubmit, watch, formState: { errors }, setValue, control } = useForm<{owner: SingleOwnerFormData[]}>({
    shouldUnregister: false,
    defaultValues: {
      owner: [
        initializedSingleOwnerForm
      ]
    }
  });
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'owner'
  });
  const { currentStep, pushNext } = usePath(isEdit);

  const onSubmit = (data: {owner: SingleOwnerFormData[]}) => {
    setIsLoading(true);
    const submitData = data.owner.map(owner => {
      const {birthday_year, birthday_month, birthday_day} = owner;
      const birthday = `${birthday_year}-${birthday_month}-${birthday_day}`;
      const is_representative = typeof owner.is_representative === 'boolean' ? owner.is_representative : owner.is_representative === 'true';
      return {
        is_representative: is_representative,
        category: owner.category,
        corporate_owning_individual: {
          last_name_kanji: owner.last_name_kanji,
          first_name_kanji: owner.first_name_kanji,
          last_name_kana: owner.last_name_kana,
          first_name_kana: owner.first_name_kana,
          corporate_relationship: owner.corporate_relationship,
          gender: owner.gender,
          birthday: birthday
        },
        corporate_owning_corporate: {
          name: owner.name,
          name_kana: owner.name_kana,
          corporate_number: owner.corporate_number,
          corporate_relationship: owner.corporate_relationship,
        },
        address: {
          postal_code: owner.postal_code,
          prefecture_name: owner.prefecture_name,
          city: owner.city,
          street: owner.street,
          building: owner.building
        }
      };
    });

    if(isEdit && !confirm(CONFIRM_MESSAGE)) {
      return setIsLoading(false);
    }
    axios.post('/api/v1/customer/corporate_registration/corporate_owner', {corporate_owners: submitData})
      .then((response) => {
        if (isEdit) {
          const message = response.data.message;
          Toast.setFlashNotice(message);
        }
        pushNext();
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    // オーナー情報と投資適合性カテゴリを並行して取得
    Promise.all([
      axios.get('/api/v1/customer/corporate_registration/corporate_owner'),
      axios.get('/api/v1/customer/investor_registration/investor_compatibility')
    ])
      .then(([ownerResponse, compatibilityResponse]) => {
        // オーナー情報の処理
        const data: {'registration_in_progress?': boolean, 'corporate_owners': SingleOwner[]} = ownerResponse.data;
        data.corporate_owners.forEach((owner, index) => {
          const ownerData: any = {
            is_representative: `${owner.is_representative}` as 'true' | 'false',
            category: owner.category,
            ...owner.address
          };

          if (ownerData.category === 'individual') {
            Object.assign(ownerData, owner.corporate_owning_individual);
          } else {
            Object.assign(ownerData, owner.corporate_owning_corporate);
          }

          if(index === 0) {
            update(0, ownerData as SingleOwnerFormData);
          } else {
            append(ownerData as SingleOwnerFormData);
          }
        });
        setIsEdit(!data['registration_in_progress?']);

        // 投資適合性カテゴリの公開状態を確認（新規登録モードの場合のみ）
        if (data['registration_in_progress?']) {
          const compatibilityData = compatibilityResponse.data;
          if (compatibilityData.investor_compatibilities.length === 0) {
            setShouldSkipCompatibility(true);
          } else {
            setShouldSkipCompatibility(false);
          }
        }
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line

  const addNewOwner = () => {
    append(initializedSingleOwnerForm);
  };
  const deleteOwner = (index: number) => {
    remove(index);
  };

  return (
    <InvestorRegistrationContainer formId={ currentStep.name } isEdit={isEdit} >
      <form onSubmit={handleSubmit(onSubmit)} className="form-wrap mt40" id={currentStep.name}>
        {fields.map((single_owner, index) => {
          return (
            <SingleCorporateOwner
              register={register}
              watch={watch}
              deleteOwner={deleteOwner}
              setValue={setValue}
              errors={errors}
              single_owner={single_owner}
              control={control}
              index={index}
              key={single_owner.id}
              isSingle={fields.length === 1}
            />
          );
        })}
        <button onClick={addNewOwner} type="button" className="form-label__button form-label__button--add" >
          <span className="material-icons form-label__link-icon form-label__link-icon--add">add_circle_outlined</span>
        実質的支配者を追加する
        </button>
      </form>
    </InvestorRegistrationContainer>
  );
};
